// Unified Course Lookup Utilities
// Provides consistent methods to find personalized courses across different test types

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";

/**
 * Get course by unified testResultId format
 * Attempts exact match, then fallback to legacy format
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} testResultId - Unified format: "{type}:{id}" or legacy format
 * @returns {Object|null} - { id: courseId, data: courseData } or null
 */
export async function getCourseByTestResultId(db, testResultId) {
  try {
    if (!testResultId) return null;

    // Attempt 1: Exact match with unified format
    const q1 = query(
      collection(db, "personalizedCourses"),
      where("testResultId", "==", testResultId),
      limit(1)
    );
    const snap1 = await getDocs(q1);
    if (!snap1.empty) {
      const doc = snap1.docs[0];
      console.log("✅ Found course via exact testResultId match:", testResultId);
      return {
        id: doc.id,
        data: doc.data(),
      };
    }

    // Attempt 2: Try legacy format (without prefix)
    // Extract ID after colon if it's a new format
    let legacyId = testResultId;
    if (testResultId.includes(":")) {
      legacyId = testResultId.split(":")[1];
    }

    if (legacyId !== testResultId) {
      const q2 = query(
        collection(db, "personalizedCourses"),
        where("testResultId", "==", legacyId),
        limit(1)
      );
      const snap2 = await getDocs(q2);
      if (!snap2.empty) {
        const doc = snap2.docs[0];
        console.log("✅ Found course via legacy testResultId format:", legacyId);
        return {
          id: doc.id,
          data: doc.data(),
        };
      }
    }

    // Attempt 3: Try with underscores/dashes variations
    const variations = [
      testResultId.replace(/-/g, "_"),
      testResultId.replace(/_/g, "-"),
    ];

    for (const variant of variations) {
      if (variant !== testResultId) {
        const q3 = query(
          collection(db, "personalizedCourses"),
          where("testResultId", "==", variant),
          limit(1)
        );
        const snap3 = await getDocs(q3);
        if (!snap3.empty) {
          const doc = snap3.docs[0];
          console.log("✅ Found course via format variation:", variant);
          return {
            id: doc.id,
            data: doc.data(),
          };
        }
      }
    }

    // No course found
    return null;
  } catch (error) {
    console.error("Error fetching course by testResultId:", error);
    return null;
  }
}

/**
 * Get course by user ID and weak areas (fallback method)
 * Finds most recent course for user that covers weak areas
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} userId - User ID
 * @param {Array<string>} weakAreas - Array of weak area strings to match
 * @returns {Object|null} - { id: courseId, data: courseData } or null
 */
export async function getCourseByWeakAreas(db, userId, weakAreas = []) {
  try {
    const q = query(
      collection(db, "personalizedCourses"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(5)
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      return null;
    }

    // If no weak areas specified, return most recent course
    if (!weakAreas || weakAreas.length === 0) {
      const doc = snap.docs[0];
      return {
        id: doc.id,
        data: doc.data(),
      };
    }

    // Find course with matching weak areas
    for (const docSnap of snap.docs) {
      const courseData = docSnap.data();
      const courseWeakAreas = courseData.basedOnMistakes || courseData.weakAreasLabels || [];

      // Check if any weak area matches
      const hasMatchingArea = weakAreas.some((area) =>
        courseWeakAreas.some((courseArea) =>
          courseArea.toLowerCase().includes(area.toLowerCase())
        )
      );

      if (hasMatchingArea) {
        return {
          id: docSnap.id,
          data: courseData,
        };
      }
    }

    // Fallback: return most recent course even if weak areas don't match exactly
    const doc = snap.docs[0];
    return {
      id: doc.id,
      data: doc.data(),
    };
  } catch (error) {
    console.error("Error fetching course by weak areas:", error);
    return null;
  }
}

/**
 * Get course by test type and user ID
 * Finds most recent course of specific test type
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} userId - User ID
 * @param {string} testType - Test type: "skills_test", "hr_test", etc.
 * @returns {Object|null} - { id: courseId, data: courseData } or null
 */
export async function getCourseByTestType(db, userId, testType) {
  try {
    const q = query(
      collection(db, "personalizedCourses"),
      where("userId", "==", userId),
      where("testType", "==", testType),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      return null;
    }

    const doc = snap.docs[0];
    return {
      id: doc.id,
      data: doc.data(),
    };
  } catch (error) {
    console.error("Error fetching course by test type:", error);
    return null;
  }
}

/**
 * Enhanced fallback lookup with 4 attempt levels
 * 1. Direct courseId lookup
 * 2. testResultId match
 * 3. Test type match
 * 4. Most recent course for user
 * 
 * @param {Object} db - Firestore database instance
 * @param {Object} params - { courseId, testResultId, testType, userId, weakAreas }
 * @returns {Object|null} - { id: courseId, data: courseData } or null
 */
export async function getCourseFallback(
  db,
  { courseId, testResultId, testType, userId, weakAreas }
) {
  // Attempt 1: Direct lookup by courseId (if provided)
  if (courseId) {
    try {
      const { getDoc, doc } = await import("firebase/firestore");
      const docSnap = await getDoc(doc(db, "personalizedCourses", courseId));
      if (docSnap.exists()) {
        return {
          id: courseId,
          data: docSnap.data(),
        };
      }
    } catch (error) {
      console.warn("Direct courseId lookup failed:", error);
    }
  }

  // Attempt 2: Lookup by testResultId
  if (testResultId) {
    const result = await getCourseByTestResultId(db, testResultId);
    if (result) {
      return result;
    }
  }

  // Attempt 3: Lookup by test type
  if (testType && userId) {
    const result = await getCourseByTestType(db, userId, testType);
    if (result) {
      return result;
    }
  }

  // Attempt 4: Lookup by weak areas or most recent course
  if (userId) {
    const result = await getCourseByWeakAreas(db, userId, weakAreas);
    if (result) {
      return result;
    }
  }

  // No course found
  return null;
}

/**
 * Migrate old courses to new format with type prefixes
 * Should be run once to update existing data
 * 
 * @param {Object} db - Firestore database instance
 * @param {Function} onProgress - Callback function (current, total) for progress tracking
 * @returns {Object} - { migrated, skipped, failed, total }
 */
export async function migrateCoursesToNewFormat(db, onProgress = null) {
  const { getDocs, collection, updateDoc, doc } = await import("firebase/firestore");
  
  try {
    console.log("🔄 Starting course migration to new format...");
    let migrated = 0;
    let skipped = 0;
    let failed = 0;
    let total = 0;

    const coursesSnapshot = await getDocs(collection(db, "personalizedCourses"));
    total = coursesSnapshot.size;
    console.log(`📊 Found ${total} courses to process`);

    for (const docSnap of coursesSnapshot.docs) {
      const courseData = docSnap.data();
      const courseId = docSnap.id;

      try {
        // Skip if already in new format (has prefix)
        if (courseData.testResultId?.includes(":")) {
          console.log(`⏭️ Skipping ${courseId} - already in new format`);
          skipped++;
          if (onProgress) onProgress(skipped + migrated + failed, total);
          continue;
        }

        // Infer testType from existing data
        let testType = courseData.testType;
        if (!testType) {
          // Guess based on fields
          if (courseData.jobTitle) {
            testType = "hr_test";
          } else if (courseData.skillCategory) {
            testType = "skills_test";
          } else {
            testType = "hr_test"; // Default fallback
          }
        }

        // Create new testResultId with prefix
        const oldTestResultId = courseData.testResultId;
        const prefix = testType.split("_")[0]; // "skills", "hr", "interview"
        const newTestResultId = `${prefix}:${oldTestResultId}`;

        // Prepare update data
        const updateData = {
          testResultId: newTestResultId,
          testType: testType,
          weakAreasLabels: courseData.basedOnMistakes || courseData.weakAreasLabels || [],
        };

        // Update the document
        await updateDoc(doc(db, "personalizedCourses", courseId), updateData);
        
        migrated++;
        console.log(`✅ Migrated ${courseId}: ${oldTestResultId} → ${newTestResultId}`);
        
        if (onProgress) onProgress(skipped + migrated + failed, total);
      } catch (error) {
        failed++;
        console.error(`❌ Failed to migrate ${courseId}:`, error);
        if (onProgress) onProgress(skipped + migrated + failed, total);
      }
    }

    const result = { migrated, skipped, failed, total };
    console.log(`✅ Migration complete:`, result);
    return result;
  } catch (error) {
    console.error("❌ Migration failed:", error);
    throw error;
  }
}

/**
 * Check migration status - shows how many courses need migration
 * 
 * @param {Object} db - Firestore database instance
 * @returns {Object} - { needsMigration, alreadyMigrated, total }
 */
export async function checkMigrationStatus(db) {
  const { getDocs, collection } = await import("firebase/firestore");
  
  try {
    const coursesSnapshot = await getDocs(collection(db, "personalizedCourses"));
    
    let needsMigration = 0;
    let alreadyMigrated = 0;

    coursesSnapshot.docs.forEach((doc) => {
      const testResultId = doc.data().testResultId;
      if (testResultId?.includes(":")) {
        alreadyMigrated++;
      } else {
        needsMigration++;
      }
    });

    return {
      needsMigration,
      alreadyMigrated,
      total: coursesSnapshot.size,
    };
  } catch (error) {
    console.error("❌ Error checking migration status:", error);
    return { needsMigration: 0, alreadyMigrated: 0, total: 0 };
  }
}
